import { template } from "@ember/template-compiler";
import { bannerError } from 'client-portal/utils/error-handling';
// eslint-disable-next-line no-restricted-imports -- the draft save must stay debounced in CP integration specs, where isTest() is true but isEmberTesting() is not
import { didCancel, task, timeout } from 'ember-concurrency';
import { session as draftStore } from 'client-portal/utils/persistent-hash-table';
import { getOwner } from '@ember/application';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { namespace } from 'client-portal/adapters/application';
import { on } from '@ember/modifier';
import { scrollToValidationError } from 'client-portal/utils/validate-record';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import AvailabilityGrid from './availability-grid';
import Component from '@glimmer/component';
import Controls from './form/controls';
import DateOfBirth from './form/date-of-birth';
import EmailFields from './form/email-fields';
import Header from './form/header';
import InsuranceType from './form/insurance-type';
import LayoutContainer from 'client-portal/components/layout/container';
import NameFields from './form/name-fields';
import OnInsert from 'client-portal/modifiers/on-insert';
import PhoneFields from './form/phone-fields';
import ReasonForVisit from './form/reason-for-visit';
import SharedPatternForm from 'client-portal/components/shared/pattern-form';
import Success from './success';
import WaitlistIntakeForm, { ALL_DAY_SLOT, IS_RECAPTCHA_DISABLED } from 'client-portal/models/waitlist-intake-form';
import moment from 'moment-timezone';
import styles from './form.module.scss';
const AUTOSAVE_KEY = 'waitlist-intake-draft';
function nextSlotsFor(currentSlots1, slot1, checked1) {
    let slots1 = [
        ...(currentSlots1 || [])
    ];
    if (!checked1) {
        return slots1.filter((item1)=>item1 !== slot1);
    }
    if (slot1 === ALL_DAY_SLOT) {
        return [
            ALL_DAY_SLOT
        ];
    }
    slots1 = slots1.filter((item1)=>item1 !== ALL_DAY_SLOT);
    return slots1.includes(slot1) ? slots1 : [
        ...slots1,
        slot1
    ];
}
export default class SiteWaitlistIntakeForm extends Component {
    @service
    store;
    @service
    analytics;
    @service
    clientTimezone;
    @service
    intl;
    @service
    recaptcha;
    styles = styles;
    @tracked
    form = null;
    @tracked
    availability = null;
    @tracked
    isComplete = false;
    constructor(owner1, args1){
        super(owner1, args1);
        let form1 = WaitlistIntakeForm.create(getOwner(this).ownerInjection(), {
            intl: this.intl
        });
        form1.restore(this.loadDraft());
        this.form = form1;
        this.availability = form1.availability;
    }
    get isAutosaveDisplayed() {
        return Boolean(this.autosaveTask.lastSuccessful) && !this.isComplete;
    }
    isRecaptchaDisabled = IS_RECAPTCHA_DISABLED;
    setupAutosave = ()=>{
        if (Object.keys(this.loadDraft()).length) {
            this.autosaveTask.perform({
                immediate: true
            });
        }
    };
    updateField = (path1, value1)=>{
        this.form.set(path1, value1);
        this.autosaveTask.perform();
    };
    toggleAvailability = (day1, slot1, checked1)=>{
        let availability1 = {
            ...this.availability,
            [day1]: nextSlotsFor(this.availability?.[day1], slot1, checked1)
        };
        this.availability = availability1;
        this.form.set('availability', availability1);
        this.autosaveTask.perform();
    };
    submit = async (event1)=>{
        event1?.preventDefault?.();
        if (!this.isRecaptchaDisabled) {
            this.form.set('recaptchaResponse', this.recaptcha.getResponse());
        }
        if (this.form.validations.isInvalid) {
            this.form.set('isDirty', true);
            await waitForRender();
            scrollToValidationError('[data-test-waitlist-intake-form]');
            return;
        }
        try {
            await this.submitTask.perform();
        } catch (err1) {
            if (!didCancel(err1)) {
                throw err1;
            }
        }
    };
    @task(function*({ immediate: immediate1 } = {}) {
        if (!immediate1) {
            yield timeout(isEmberTesting() ? 0 : 1000);
        }
        let payload1 = this.form.serialize();
        draftStore.set(AUTOSAVE_KEY, JSON.stringify({
            ...payload1,
            _timestamp: Date.now()
        }));
        return moment.tz(this.clientTimezone.timezone);
    }).restartable()
    autosaveTask;
    // Plain JSON POST — same pattern as contact-request (no auth, not JSON:API).
    // 201 is success including duplicate-email PHI-hiding; do not special-case duplicates.
    @task(function*() {
        let payload1 = this.form.serializeForApi();
        try {
            let adapter1 = this.store.adapterFor('application');
            yield adapter1.ajax(`/${namespace}/waitlist-intakes`, 'POST', {
                data: {
                    waitlistIntake: payload1
                }
            });
            this.analytics.trackAnonymously('Client Added To Waitlist', {
                object: 'Client',
                action: 'Added To Waitlist'
            });
            this.autosaveTask.cancelAll();
            draftStore.remove(AUTOSAVE_KEY);
            this.isComplete = true;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } catch (err1) {
            let title1 = err1?.errors?.[0]?.title;
            if (typeof title1 === 'string' && title1.includes('reCAPTCHA verification failed')) {
                yield this.recaptcha.reset();
            }
            bannerError(err1, {
                title: this.intl.t('messages.error.waitlistRequestNotSubmitted')
            });
        }
    }).drop()
    submitTask;
    loadDraft() {
        try {
            let data1 = JSON.parse(draftStore.get(AUTOSAVE_KEY));
            if (data1 && typeof data1 === 'object') {
                // eslint-disable-next-line no-unused-vars
                let { _timestamp: _timestamp1, ...draft1 } = data1;
                return draft1;
            }
        } catch (err1) {
        // ignore corrupt drafts
        }
        return {};
    }
    static{
        template(`
    <div class={{this.styles.component}} {{OnInsert this.setupAutosave}}>
      <LayoutContainer class="large">
        <Header
          @isComplete={{this.isComplete}}
          @isAutosaveDisplayed={{this.isAutosaveDisplayed}}
          @autosaveTask={{this.autosaveTask}}
        />

        {{#if this.isComplete}}
          <Success data-test-waitlist-intake-complete />
        {{else}}
          <form {{on "submit" this.submit}} data-test-waitlist-intake-form>
            <SharedPatternForm class="form-container">
              <NameFields @form={{this.form}} @onUpdate={{this.updateField}} />
              <DateOfBirth @form={{this.form}} @onUpdate={{this.updateField}} />
              <InsuranceType @form={{this.form}} @onUpdate={{this.updateField}} />
              <EmailFields @form={{this.form}} @onUpdate={{this.updateField}} />
              <PhoneFields @form={{this.form}} @onUpdate={{this.updateField}} />
              <ReasonForVisit @form={{this.form}} @onUpdate={{this.updateField}} />

              <div class="row">
                <div class="col-12">
                  <AvailabilityGrid
                    @form={{this.form}}
                    @availability={{this.availability}}
                    @onToggle={{this.toggleAvailability}}
                  />
                </div>
              </div>
            </SharedPatternForm>

            <hr />

            <Controls
              @form={{this.form}}
              @isRecaptchaDisabled={{this.isRecaptchaDisabled}}
              @isSubmitting={{this.submitTask.isRunning}}
            />
          </form>
        {{/if}}
      </LayoutContainer>
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
